import { useUserStore } from '@/stores/userStore';
import type { LDClient } from 'launchdarkly-js-client-sdk';
import { isEqual } from 'lodash-es';
import type { LDPluginOptions } from 'launchdarkly-vue-client-sdk';
import { getRoleStr, ROLES, type ApiV1, type ApiV2, type Role } from '@teamworksdev/influencer/core';
import { getLDClient, INIT_TIMEOUT_SEC, getLDClientWhenReady } from '@/plugins/ldClientBridge';

const INFLUENCER_LAUNCHDARKLY_ANONYMOUS_USER_KEY = 'influencer-launchdarkly-anonymous-user';

export const ldConfig: LDPluginOptions = {
	context: {
		kind: 'user',
		key: INFLUENCER_LAUNCHDARKLY_ANONYMOUS_USER_KEY,
		anonymous: true,
	},
	clientSideID: import.meta.env.VITE_LD_CLIENT_ID,
	streaming: true,
	options: {
		sendEventsOnlyForVariation: true,
	},
};

export interface LDUserContext {
	kind: 'multi';
	user: {
		key: string;
		name?: string;
		email?: string;
	};
	profile?: {
		key: string;
		profileType: 'compliance' | 'team' | 'brand' | 'unknown';
		name: string;
	};
	organization?: {
		key: string;
		name?: string;
		type: 'school' | 'team' | 'brand';
		[attribute: string]: any;
	};
}

function getProfileType(
	profile: ApiV1.FullUserProfile,
): 'team' | 'compliance' | 'brand' | 'unknown' {
	const map = new Map<Role, 'team' | 'compliance' | 'brand'>([
		[ROLES.BRAND_USER, 'brand'],
		[ROLES.COMPLIANCE_OFFICER, 'compliance'],
		[ROLES.TEAM_USER, 'team'],
	]);

	return map.get(profile.role_id) ?? 'unknown';
}

export function buildUserContext(
	user: ApiV2.User,
	profile?: ApiV1.FullUserProfile,
): LDUserContext {
	const context: LDUserContext = {
		kind: 'multi',
		user: {
			key: user.uuid,
			email: user.email,
			name: `${user.first_name} ${user.last_name}`,
		},
	};

	// Add profile context if profile is provided
	if (profile) {
		context.profile = {
			key: profile.uuid,
			profileType: getProfileType(profile),
			name: getRoleStr(profile.role_id),
		};

		// Add organization context based on profile type
		switch (profile.role_id) {
			case ROLES.COMPLIANCE_OFFICER:
				context.organization = {
					key: profile.data.school.uuid,
					name: profile.data.school.name,
					schoolId: profile.data.school.uuid,
					schoolName: profile.data.school.name,
					type: 'school',
				};
				break;
			case ROLES.TEAM_USER:
				context.organization = {
					key: profile.data.team.uuid || profile.data.school.uuid,
					name: profile.data.team.name || profile.data.school.name,
					type: profile.data.team.uuid ? 'team' : 'school',
					schoolId: profile.data.school.uuid,
					schoolName: profile.data.school.name,
				};
				break;
			case ROLES.BRAND_USER:
				context.organization = {
					key: profile.data.exchange_brand.uuid,
					name: profile.data.exchange_brand.name,
					type: 'brand',
				};
				break;
			default:
				break;
		}
	}

	return context;
}

let lastAppliedContext: LDUserContext | undefined = undefined;

/** Serializes updateLDContext so concurrent callers do not both pass the lastAppliedContext dedupe before either identify completes. */
let ldContextChain: Promise<unknown> = Promise.resolve();

export async function getClient() {
	return getLDClientWhenReady();
}

function identifyWithCallback(client: LDClient, context: LDUserContext): Promise<void> {
	return new Promise((resolve, reject) => {
		client.identify(context, undefined, (err) => {
			if (err) {
				console.error('[LaunchDarkly][updateLDContext] Error identifying user:', err);
				reject(err);
				return;
			}
			resolve();
		});
	});
}

async function runUpdateLDContext(): Promise<LDClient> {
	const userStore = useUserStore();

	if (!userStore.user) {
		console.warn('[LaunchDarkly][getLDClient] User context missing');
		return getLDClientWhenReady();
	}

	const newContext = buildUserContext(
		userStore.user,
		userStore.profile,
	);

	const client = getLDClient();

	if (isEqual(lastAppliedContext, newContext)) {
		await client.waitForInitialization(INIT_TIMEOUT_SEC);
		return client;
	}

	try {
		await identifyWithCallback(client, newContext);
		lastAppliedContext = newContext;
	} catch {
		// Logged in identifyWithCallback; continue with previous context for flag reads
	}

	await client.waitForInitialization(INIT_TIMEOUT_SEC);

	return client;
}

export async function updateLDContext(): Promise<LDClient> {
	const result = ldContextChain.then(() => runUpdateLDContext());
	ldContextChain = result.then(
		() => undefined,
		() => undefined,
	);
	return result;
}
